import { Campaign, CampaignTarget } from '../../types/campaign';
import { ZOHO_CONFIG } from '../../config/zoho';
import { sampleCampaigns } from '../../data/sampleCampaigns';

export class ZohoClient {
  private static instance: ZohoClient;
  private apiKey: string;
  private baseUrl: string;
  private isDev: boolean;

  private constructor() {
    this.apiKey = ZOHO_CONFIG.apiKey;
    this.baseUrl = ZOHO_CONFIG.baseUrl;
    this.isDev = import.meta.env.DEV;
  }

  public static getInstance(): ZohoClient {
    if (!ZohoClient.instance) {
      ZohoClient.instance = new ZohoClient();
    }
    return ZohoClient.instance;
  }

  async listCampaigns(filters?: {
    status?: string;
    type?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<Campaign[]> {
    const queryParams = new URLSearchParams(filters as Record<string, string>);
    return this.request<Campaign[]>(`${ZOHO_CONFIG.endpoints.campaigns}?${queryParams}`);
  }

  async createCampaign(campaign: Omit<Campaign, 'id'>): Promise<Campaign> {
    return this.request<Campaign>(ZOHO_CONFIG.endpoints.campaigns, {
      method: 'POST',
      body: JSON.stringify(campaign),
    });
  }

  async updateCampaign(campaignId: string, updates: Partial<Campaign>): Promise<Campaign> {
    return this.request<Campaign>(`${ZOHO_CONFIG.endpoints.campaigns}/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    await this.request(`${ZOHO_CONFIG.endpoints.campaigns}/${campaignId}`, {
      method: 'DELETE',
    });
  }

  async getCampaign(campaignId: string): Promise<Campaign> {
    return this.request<Campaign>(`${ZOHO_CONFIG.endpoints.campaigns}/${campaignId}`);
  }

  async getCampaignMetrics(campaignId: string): Promise<Campaign['metrics']> {
    return this.request<Campaign['metrics']>(`${ZOHO_CONFIG.endpoints.campaigns}/${campaignId}/metrics`);
  }

  async getTargetAnalytics(campaignId: string): Promise<any> {
    return this.request(`${ZOHO_CONFIG.endpoints.campaigns}/${campaignId}/analytics`);
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    if (this.isDev) {
      await new Promise(resolve => setTimeout(resolve, 500));
      return this.getMockData<T>(endpoint);
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });

      if (!response.ok) {
        throw new Error(`Zoho API Error: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  private getMockData<T>(endpoint: string): T {
    if (endpoint.includes('/campaigns')) {
      return sampleCampaigns as T;
    }
    if (endpoint.includes('/metrics')) {
      return {
        totalTargets: 25,
        contacted: 15,
        responded: 8,
        meetings: 4,
        declined: 3,
        converted: 1,
        responseRate: 53.33,
        conversionRate: 6.67
      } as T;
    }
    if (endpoint.includes('/analytics')) {
      return {
        trends: {
          responseRates: [
            { date: '2024-03-01', rate: 45 },
            { date: '2024-03-02', rate: 52 }
          ],
          conversionRates: [
            { date: '2024-03-01', rate: 5 },
            { date: '2024-03-02', rate: 7 }
          ]
        },
        breakdown: {
          byStatus: { contacted: 15, responded: 8 },
          byPriority: { high: 10, medium: 10, low: 5 },
          byLocation: { 'San Francisco': 12, 'New York': 8, 'Boston': 5 }
        }
      } as T;
    }
    return {} as T;
  }
}